import { FormEvent, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { BookOpen, CircleDollarSign, CreditCard, Loader2, ReceiptText, RefreshCw, Ticket } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { creditsApi } from '@/api/creditsApi'
import UsageOverview from '@/pages/billing/UsageOverview'
import { ProjectStatusBadge } from '@/components/project/ProjectStatusBadge'
import { ApiError, friendlyMessage } from '@/api/http'
import { projectApi } from '@/api/projectApi'
import { CreditsInfinity } from '@/layouts/AppShell'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { useAuthStore } from '@/stores/authStore'
import type { Project } from '@/types/nf'
import { formatCount, formatCredits, formatDateTime } from '@/utils/format'

const LEDGER_LIMIT = 25
const TOPUP_PACKAGES = [
  { key: 'starter', price: '$9.90' },
  { key: 'studio', price: '$29.90' },
] as const

function numericField(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

function projectWords(project: Project): number {
  return numericField(project.current_words) ?? numericField(project.word_count) ?? 0
}

function ErrorBlock({ message, onRetry }: { message: string; onRetry: () => void }) {
  const { t } = useTranslation()
  return (
    <div className="flex flex-col gap-3 rounded-md border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive sm:flex-row sm:items-center sm:justify-between">
      <span>{message}</span>
      <Button variant="outline" size="sm" onClick={onRetry}>
        <RefreshCw className="size-4" />
        {t('common.retry')}
      </Button>
    </div>
  )
}

function TableSkeleton({ rows = 4, columns = 3 }: { rows?: number; columns?: number }) {
  return (
    <div className="space-y-3">
      {Array.from({ length: rows }).map((_, row) => (
        <div key={row} className="grid gap-3" style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}>
          {Array.from({ length: columns }).map((__, column) => (
            <Skeleton key={column} className="h-4 w-full" />
          ))}
        </div>
      ))}
    </div>
  )
}

export default function BillingUsage() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const userId = useAuthStore((s) => s.user?.user_id)
  const [ledgerOffset, setLedgerOffset] = useState(0)
  const [redeemCode, setRedeemCode] = useState('')

  const projectsQuery = useQuery({
    queryKey: ['billing', 'projects', userId],
    queryFn: () => projectApi.list(userId!),
    enabled: Boolean(userId),
    staleTime: 60_000,
  })

  const balanceQuery = useQuery({
    queryKey: ['billing', 'credits', 'balance', userId],
    queryFn: () => creditsApi.balance(userId),
    enabled: Boolean(userId),
    staleTime: 60_000,
  })

  const ledgerQuery = useQuery({
    queryKey: ['billing', 'credits', 'ledger', userId, LEDGER_LIMIT, ledgerOffset],
    queryFn: () => creditsApi.ledger(LEDGER_LIMIT, ledgerOffset, userId),
    enabled: Boolean(userId),
    staleTime: 30_000,
  })

  const paymentStatusQuery = useQuery({
    queryKey: ['billing', 'payment', 'status', userId],
    queryFn: creditsApi.paymentStatus,
    enabled: Boolean(userId),
    staleTime: 60_000,
  })

  const redeemMutation = useMutation({
    mutationFn: (code: string) => creditsApi.redeem({ code }, userId),
    onSuccess: async () => {
      setRedeemCode('')
      toast.success(t('billing.redeem.success'))
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['billing', 'credits', 'balance', userId] }),
        queryClient.invalidateQueries({ queryKey: ['billing', 'credits', 'ledger', userId] }),
      ])
    },
    onError: (error) => {
      toast.error(getRedeemErrorMessage(error, t))
    },
  })

  const projects = projectsQuery.data ?? []
  const totalWords = useMemo(() => projects.reduce((sum, project) => sum + projectWords(project), 0), [projects])
  const transactions = ledgerQuery.data?.transactions ?? []
  const hasNextLedgerPage = transactions.length >= LEDGER_LIMIT
  const paymentEnabled = paymentStatusQuery.data?.enabled === true

  function handleRedeemSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const code = redeemCode.trim()
    if (!code) {
      toast.error(t('billing.redeem.required'))
      return
    }
    redeemMutation.mutate(code)
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div className="flex flex-col gap-2">
        <h1 className="text-2xl font-semibold tracking-normal">{t('billing.title')}</h1>
        <p className="max-w-3xl text-sm text-muted-foreground">{t('billing.description')}</p>
      </div>

      <UsageOverview userId={userId} />

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1.35fr)_minmax(320px,0.65fr)]">
        <Card className="rounded-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BookOpen className="size-5 text-primary" />
              {t('billing.projects.title')}
            </CardTitle>
            <CardDescription>{t('billing.projects.description')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            {projectsQuery.isLoading ? (
              <div className="grid gap-3 sm:grid-cols-2">
                <Skeleton className="h-20" />
                <Skeleton className="h-20" />
              </div>
            ) : projectsQuery.isError ? (
              <ErrorBlock message={t('billing.projects.error')} onRetry={() => void projectsQuery.refetch()} />
            ) : (
              <>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="rounded-md border bg-muted/30 p-4">
                    <p className="text-sm text-muted-foreground">{t('billing.projects.count')}</p>
                    <p className="nf-mono mt-2 text-2xl font-semibold">{formatCount(projects.length)}</p>
                  </div>
                  <div className="rounded-md border bg-muted/30 p-4">
                    <p className="text-sm text-muted-foreground">{t('billing.projects.totalWords')}</p>
                    <p className="nf-mono mt-2 text-2xl font-semibold">{formatCount(totalWords)}</p>
                  </div>
                </div>

                {projects.length === 0 ? (
                  <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
                    {t('billing.projects.empty')}
                  </div>
                ) : (
                  <div className="overflow-x-auto rounded-md border">
                    <table className="w-full min-w-[520px] text-sm">
                      <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
                        <tr>
                          <th className="px-4 py-3 font-medium">{t('billing.projects.table.title')}</th>
                          <th className="px-4 py-3 text-right font-medium">{t('billing.projects.table.words')}</th>
                          <th className="px-4 py-3 font-medium">{t('billing.projects.table.status')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {projects.map((project) => (
                          <tr key={project.id} className="border-t">
                            <td className="max-w-[360px] truncate px-4 py-3 font-medium">{project.title}</td>
                            <td className="nf-mono px-4 py-3 text-right">{formatCount(projectWords(project))}</td>
                            <td className="px-4 py-3">
                              <ProjectStatusBadge status={project.status} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        <Card className="rounded-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CircleDollarSign className="size-5 text-primary" />
              {t('billing.credits.title')}
            </CardTitle>
            <CardDescription>{t('billing.credits.description')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            {balanceQuery.isLoading ? (
              <div className="space-y-3">
                <Skeleton className="h-8 w-32" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
              </div>
            ) : balanceQuery.isError ? (
              <ErrorBlock message={t('billing.credits.error')} onRetry={() => void balanceQuery.refetch()} />
            ) : balanceQuery.data?.enabled === false ? (
              <div className="flex items-center justify-between rounded-md border bg-muted/30 p-4">
                <div>
                  <p className="text-sm text-muted-foreground">{t('billing.credits.available')}</p>
                  <p className="mt-2 flex items-center gap-2 text-3xl font-semibold">
                    <CreditsInfinity label={t('shell.creditsUnlimitedLabel')} />
                    <span className="text-sm font-normal text-muted-foreground">{t('billing.credits.disabled')}</span>
                  </p>
                </div>
              </div>
            ) : (
              <div className="grid gap-3">
                {(['available', 'balance', 'reserved'] as const).map((key) => (
                  <div key={key} className="flex items-center justify-between rounded-md border bg-muted/30 p-4">
                    <span className="text-sm text-muted-foreground">{t(`billing.credits.${key}`)}</span>
                    <span className="nf-mono text-lg font-semibold">{formatCredits(balanceQuery.data?.[key] as number | undefined)}</span>
                  </div>
                ))}
              </div>
            )}

            <form className="space-y-3 rounded-md border bg-muted/20 p-4" onSubmit={handleRedeemSubmit}>
              <div className="flex items-center gap-2">
                <Ticket className="size-4 text-primary" />
                <p className="text-sm font-medium">{t('billing.redeem.title')}</p>
              </div>
              <div className="flex flex-col gap-2 sm:flex-row">
                <Input
                  value={redeemCode}
                  onChange={(event) => setRedeemCode(event.target.value)}
                  placeholder={t('billing.redeem.placeholder')}
                  autoComplete="off"
                  className="nf-mono"
                />
                <Button type="submit" disabled={redeemMutation.isPending || !redeemCode.trim()} className="sm:w-28">
                  {redeemMutation.isPending ? <Loader2 className="size-4 animate-spin" /> : <Ticket className="size-4" />}
                  {t('billing.redeem.submit')}
                </Button>
              </div>
            </form>

            <div className="space-y-3 rounded-md border bg-muted/20 p-4">
              <div className="flex items-center gap-2">
                <CreditCard className="size-4 text-primary" />
                <p className="text-sm font-medium">{t('billing.payment.title')}</p>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                {TOPUP_PACKAGES.map((pack) => (
                  <div key={pack.key} className="rounded-md border bg-background/60 p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-medium">{t(`billing.payment.packages.${pack.key}.title`)}</p>
                        <p className="mt-1 text-xs text-muted-foreground">{t(`billing.payment.packages.${pack.key}.description`)}</p>
                      </div>
                      <Badge variant="secondary" className="rounded-md">
                        {pack.price}
                      </Badge>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="mt-3 w-full justify-start"
                      onClick={() => toast.info(t('billing.payment.preregistered'))}
                    >
                      <Ticket className="size-4" />
                      {t('billing.payment.preregister')}
                    </Button>
                  </div>
                ))}
              </div>
              <Button variant="outline" disabled={!paymentEnabled} className="w-full justify-start">
                <CreditCard className="size-4" />
                {paymentEnabled ? t('billing.payment.checkout') : t('billing.payment.comingSoon')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="rounded-lg">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ReceiptText className="size-5 text-primary" />
            {t('billing.ledger.title')}
          </CardTitle>
          <CardDescription>{t('billing.ledger.description')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {ledgerQuery.isLoading ? (
            <TableSkeleton rows={5} columns={4} />
          ) : ledgerQuery.isError ? (
            <ErrorBlock message={t('billing.ledger.error')} onRetry={() => void ledgerQuery.refetch()} />
          ) : transactions.length === 0 ? (
            <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
              {t('billing.ledger.empty')}
            </div>
          ) : (
            <div className="overflow-x-auto rounded-md border">
              <table className="w-full min-w-[640px] text-sm">
                <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3 font-medium">{t('billing.ledger.table.type')}</th>
                    <th className="px-4 py-3 text-right font-medium">{t('billing.ledger.table.amount')}</th>
                    <th className="px-4 py-3 text-right font-medium">{t('billing.ledger.table.balanceAfter')}</th>
                    <th className="px-4 py-3 font-medium">{t('billing.ledger.table.createdAt')}</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((tx, index) => (
                    <tr key={`${tx.created_at}-${index}`} className="border-t">
                      <td className="px-4 py-3">
                        <Badge variant="outline">{t(`billing.ledger.types.${tx.type}`, { defaultValue: tx.type })}</Badge>
                      </td>
                      <td className="nf-mono px-4 py-3 text-right">{formatCredits(tx.amount)}</td>
                      <td className="nf-mono px-4 py-3 text-right">{formatCredits(tx.balance_after)}</td>
                      <td className="nf-mono px-4 py-3">{formatDateTime(tx.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex items-center justify-between gap-3">
            <span className="text-xs text-muted-foreground">
              {transactions.length === 1
                ? t('billing.ledger.single', { index: ledgerOffset + 1 })
                : t('billing.ledger.range', { start: ledgerOffset + 1, end: ledgerOffset + transactions.length })}
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={ledgerOffset === 0 || ledgerQuery.isFetching}
                onClick={() => setLedgerOffset((offset) => Math.max(0, offset - LEDGER_LIMIT))}
              >
                {t('billing.ledger.previous')}
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={!hasNextLedgerPage || ledgerQuery.isFetching}
                onClick={() => setLedgerOffset((offset) => offset + LEDGER_LIMIT)}
              >
                {t('billing.ledger.next')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

function getRedeemErrorMessage(error: unknown, t: ReturnType<typeof useTranslation>['t']): string {
  if (error instanceof ApiError) {
    if (error.code === 'redemption_code_invalid' || error.status === 404) return t('billing.redeem.errors.invalid')
    if (error.code === 'redemption_code_used' || error.status === 409) return t('billing.redeem.errors.used')
    if (error.code === 'redemption_code_expired' || error.status === 410) return t('billing.redeem.errors.expired')
  }
  return friendlyMessage(error, t('common.serviceError'))
}
